<a mat-button routerLink="/payments/requests" class="back">
  <mat-icon>arrow_back</mat-icon> All payment requests
</a>

@if (loading && !request) {
<div class="center"><mat-spinner diameter="28"></mat-spinner></div>
} @else if (error) {
<div class="center err">{{ error }}</div>
} @else if (request) {

<section class="summary">
  <div class="summary-head">
    <div>
      <h2><span class="supplier-link" (click)="openSupplierInfoDialog()">{{ request.supplier_name }}</span></h2>
      <p class="muted">Requested {{ request.created_at | date:'medium' }}
        @if (request.closed_at) { · closed {{ request.closed_at | date:'medium' }} }</p>
    </div>
    <span class="chip" [ngClass]="statusClass(request.status)">{{ request.status }}</span>
  </div>

  <div class="pay-panel">
    <div class="pay-amount">
      <span class="label">Requested</span>
      <span class="value">{{ request.amount | number }} <small>Toman</small></span>
      @if (request.payout_amount) {
      <span class="sub coin" dir="ltr">= {{ request.payout_amount }} {{ request.payout_currency }}
        at {{ +request.payout_rate! | number }} Toman ({{ request.payout_rate_source }})</span>
      }
      <span class="sub">{{ request.runs.length }} run(s) · {{ request.contracts.length }} manual contract(s)</span>
      @if (request.original_amount != null) {
      <span class="sub warn">Supplier asked {{ request.original_amount | number }}
        @if (request.original_payout_amount) { ({{ request.original_payout_amount }} {{ request.payout_currency }}) }
        · updated {{ request.amount_updated_at | date:'short' }}</span>
      }
    </div>
    <div class="pay-card">
      <span class="label">Pay to · <bdi>{{ request.payout_method_name || 'unknown method' }}</bdi>@if (request.payout_label) { · <bdi>{{ request.payout_label }}</bdi> }</span>
      <span class="card-number" [class.address]="request.payout_kind === 'Wallet'" dir="ltr">{{ request.payout_account || '—' }}</span>
      @if (request.payout_kind !== 'Wallet') {
      <span class="sub">{{ request.payout_holder || 'No holder name' }}</span>
      }
      @if (request.destination_changed) {
      <span class="sub warn">Edited since asking (now {{ request.current_payout_account }})</span>
      } @else if (request.destination_deleted) {
      <span class="sub warn">Deleted by the supplier since asking</span>
      }
    </div>
    @if (canEdit && isPending && request.payable && !payoutInFlight) {
    <div class="pay-actions">
      @if (canSendOnChain) {
      <button mat-flat-button color="primary" type="button" [disabled]="sendingOnChain" (click)="sendOnChain()">
        <mat-icon>send</mat-icon> Send {{ request.payout_amount }} {{ request.payout_currency }}
      </button>
      <button mat-stroked-button type="button" (click)="pay()" matTooltip="Record a payment you sent yourself">
        <mat-icon>payments</mat-icon> Paid by hand
      </button>
      } @else {
      <button mat-flat-button color="primary" type="button" (click)="pay()">
        <mat-icon>payments</mat-icon> Pay {{ request.amount | number }}
      </button>
      }
    </div>
    }
  </div>

  @if (payout; as p) {
  <div class="onchain" [class.in-flight]="payoutInFlight">
    <div class="onchain-head">
      <mat-icon>link</mat-icon>
      <span>On-chain payout · {{ p.amount }} USDT ({{ p.network }})@if (p.auto_paid) { · auto-pay }</span>
      <span class="chip" [ngClass]="statusClass(p.status)">{{ payoutLabel(p) }}</span>
      @if (payoutInFlight) { <mat-spinner diameter="16"></mat-spinner> }
    </div>
    @if (p.tx_hash) {
    <div class="onchain-row" dir="ltr">
      <span class="muted">Tx</span>
      @if (p.explorer_url) {
      <a class="mono" [href]="p.explorer_url" target="_blank" rel="noopener">{{ p.tx_hash }}</a>
      } @else {
      <span class="mono">{{ p.tx_hash }}</span>
      }
    </div>
    }
    <div class="onchain-row muted">
      Queued {{ p.created_at | date:'short' }}
      @if (p.signed_at) { · sent {{ p.signed_at | date:'short' }} }
      @if (p.confirmed_at) { · mined {{ p.confirmed_at | date:'short' }}@if (p.block_number) { in block {{ p.block_number }} } }
      @if (p.gas_fee) { · gas {{ p.gas_fee }} BNB }
    </div>
    @if (p.error) {
    <p class="onchain-error"><mat-icon>error_outline</mat-icon><span>{{ p.error }}</span></p>
    }
    @if (payoutUnrecorded && isPending) {
    <p class="onchain-error"><mat-icon>warning</mat-icon>
      <span>The USDT was sent, but the payment is not recorded. Fix what blocked it and record it with Pay
        (use the tx hash as the reference); it will not be sent again.</span></p>
    }
    @if (canEdit && p.status === 'Queued') {
    <button mat-button type="button" color="warn" [disabled]="cancellingPayout" (click)="cancelPayout()">Cancel before it is sent</button>
    }
  </div>
  }

  @if (isPending && otherPending.length > 0) {
  <div class="other-pending">
    <mat-icon>info</mat-icon>
    <div>
      {{ request.supplier_name }} has {{ otherPending.length }} other pending request(s). They are not duplicates:
      each covers different runs and contracts, and each is paid on its own.
      <ul>
        @for (o of otherPending; track o.id) {
        <li><a [routerLink]="['/payments/requests', o.id]">{{ o.amount | number }} Toman</a>
          · {{ o.created_at | date:'short' }}@if (o.payout_method_name) { · <bdi>{{ o.payout_method_name }}</bdi> }</li>
        }
      </ul>
    </div>
  </div>
  }

  @if (isPending && !request.payable && request.blocked_reason) {
  <div class="blocked-row">
    <p class="blocked">
      <mat-icon>block</mat-icon>
      <span>
        @if (request.amount_updatable) {
        What this request covers changed value since it was sent: it is worth {{ request.current_amount | number }} now.
        Update it to that amount to pay it, or reject it.
        } @else {
        {{ request.blocked_reason }}
        @if (request.current_amount !== request.amount && request.runs.length + request.contracts.length > 0) {
        (worth {{ request.current_amount | number }} now)
        }
        }
      </span>
    </p>
    @if (canEdit && request.amount_updatable) {
    <button mat-stroked-button type="button" class="update-btn" [disabled]="updatingAmount" (click)="updateAmount()">
      <mat-icon>sync</mat-icon> Update to {{ request.current_amount | number }}
    </button>
    }
  </div>
  }

  @if (request.status === 'Fulfilled') {
  <p class="paid-note">
    <mat-icon>check_circle</mat-icon>
    Paid{{ request.closed_at ? ' on ' + (request.closed_at | date:'medium') : '' }} —
    <a routerLink="/payments/settlements">see it in Settlements</a>
  </p>
  }

  @if (request.status === 'Rejected' && request.admin_note) {
  <p class="rejected-note"><b>Rejected:</b> {{ request.admin_note }}</p>
  }
  @if (request.supplier_note) {
  <p class="supplier-note"><span class="muted">Supplier's note:</span> {{ request.supplier_note }}</p>
  }

  <div class="note-editor">
    <mat-form-field appearance="outline" subscriptSizing="dynamic" class="note-field">
      <mat-label>Your note (the supplier can read it)</mat-label>
      <textarea matInput rows="2" [maxlength]="maxNoteLength" [(ngModel)]="adminNote"></textarea>
      <mat-hint align="end">{{ adminNote.length }} / {{ maxNoteLength }}</mat-hint>
    </mat-form-field>
    @if (canEdit) {
    <div class="note-actions">
      <button mat-stroked-button type="button" [disabled]="savingNote || adminNote === (request.admin_note ?? '')"
        (click)="saveNote()">Save note</button>
      @if (isPending && !payoutInFlight) {
      <button mat-button type="button" color="warn" [disabled]="rejecting" (click)="rejectRequest()">Reject…</button>
      <button mat-button type="button" [disabled]="cancelling" (click)="cancelRequest()">Cancel request</button>
      }
    </div>
    }
  </div>
</section>

<p class="hint">
  <mat-icon>info</mat-icon>
  This request covers only what was owed when it was sent. Anything finished since is paid with the supplier's next request.
</p>

<h3 class="section-title">Transfer runs <span class="muted">({{ request.runs.length }} · {{ runsRequested | number }})</span></h3>
@if (request.runs.length === 0) {
<div class="center small">No transfer runs in this request.</div>
} @else if (isMobile$ | async) {
<div class="cards">
  @for (r of request.runs; track r.id) {
  <div class="card">
    <div class="card-head">
      <div class="card-title">
        <span class="mono">{{ r.account_email }}</span>
        <p class="card-meta">{{ (r.confirmed_at || r.started_at) | date:'short' }} · {{ r.status }}</p>
      </div>
      <span class="chip" [ngClass]="statusClass(r.payment_status)">{{ paymentLabel(r.payment_status) }}</span>
    </div>
    <div class="card-stats">
      <div class="stat"><span class="label">Coins</span><span class="value">{{ r.coins_transferred | number }}</span></div>
      <div class="stat"><span class="label">Fee</span><span class="value">{{ r.fee_snapshot | number }}</span></div>
      <div class="stat">
        <span class="label">Requested</span><span class="value strong">{{ r.requested_amount | number }}</span>
        @if (r.payable_amount !== r.requested_amount) {
        <span class="now drift">now {{ r.payable_amount | number }}</span>
        }
      </div>
    </div>
  </div>
  }
</div>
} @else {
<div class="table-wrap">
  <table mat-table [dataSource]="request.runs">
    <ng-container matColumnDef="date">
      <th mat-header-cell *matHeaderCellDef>Confirmed</th>
      <td mat-cell *matCellDef="let r">{{ (r.confirmed_at || r.started_at) | date:'short' }}<div class="sub">{{ r.status }}</div></td>
    </ng-container>
    <ng-container matColumnDef="account">
      <th mat-header-cell *matHeaderCellDef>Account</th>
      <td mat-cell *matCellDef="let r"><span class="mono">{{ r.account_email }}</span></td>
    </ng-container>
    <ng-container matColumnDef="coins">
      <th mat-header-cell *matHeaderCellDef>Coins</th>
      <td mat-cell *matCellDef="let r" class="num">{{ r.coins_transferred | number }}</td>
    </ng-container>
    <ng-container matColumnDef="fee">
      <th mat-header-cell *matHeaderCellDef>Fee snapshot</th>
      <td mat-cell *matCellDef="let r" class="num">{{ r.fee_snapshot | number }}</td>
    </ng-container>
    <ng-container matColumnDef="requested">
      <th mat-header-cell *matHeaderCellDef>Requested</th>
      <td mat-cell *matCellDef="let r" class="num strong">{{ r.requested_amount | number }}</td>
    </ng-container>
    <ng-container matColumnDef="now">
      <th mat-header-cell *matHeaderCellDef>Now</th>
      <td mat-cell *matCellDef="let r" class="num" [class.drift]="r.payable_amount !== r.requested_amount">{{ r.payable_amount | number }}</td>
    </ng-container>
    <ng-container matColumnDef="payment">
      <th mat-header-cell *matHeaderCellDef>Payment</th>
      <td mat-cell *matCellDef="let r"><span class="chip" [ngClass]="statusClass(r.payment_status)">{{ paymentLabel(r.payment_status) }}</span></td>
    </ng-container>
    <tr mat-header-row *matHeaderRowDef="runColumns"></tr>
    <tr mat-row *matRowDef="let row; columns: runColumns;"></tr>
  </table>
</div>
}

<h3 class="section-title">Manual contracts <span class="muted">({{ request.contracts.length }} · {{ contractsRequested | number }})</span></h3>
@if (request.contracts.length === 0) {
<div class="center small">No manual contracts in this request.</div>
} @else if (isMobile$ | async) {
<div class="cards">
  @for (c of request.contracts; track c.id) {
  <div class="card">
    <div class="card-head">
      <div class="card-title">
        {{ contractLabel(c) }}
        <p class="card-meta">{{ c.created_at | date:'short' }} · {{ c.status }}</p>
      </div>
      <span class="chip" [ngClass]="statusClass(c.payment_status)">{{ paymentLabel(c.payment_status) }}</span>
    </div>
    <div class="card-stats">
      <div class="stat"><span class="label">Coins</span><span class="value">{{ c.received_coins | number }}</span></div>
      <div class="stat"><span class="label">Fee</span><span class="value">{{ c.fee | number }}</span></div>
      <div class="stat">
        <span class="label">Requested</span><span class="value strong">{{ c.requested_amount | number }}</span>
        @if (c.payable_amount !== c.requested_amount) {
        <span class="now drift">now {{ c.payable_amount | number }}</span>
        }
      </div>
    </div>
  </div>
  }
</div>
} @else {
<div class="table-wrap">
  <table mat-table [dataSource]="request.contracts">
    <ng-container matColumnDef="date">
      <th mat-header-cell *matHeaderCellDef>Created</th>
      <td mat-cell *matCellDef="let c">{{ c.created_at | date:'short' }}<div class="sub">{{ c.status }}</div></td>
    </ng-container>
    <ng-container matColumnDef="name">
      <th mat-header-cell *matHeaderCellDef>Contract</th>
      <td mat-cell *matCellDef="let c">{{ contractLabel(c) }}</td>
    </ng-container>
    <ng-container matColumnDef="coins">
      <th mat-header-cell *matHeaderCellDef>Coins</th>
      <td mat-cell *matCellDef="let c" class="num">{{ c.received_coins | number }}</td>
    </ng-container>
    <ng-container matColumnDef="fee">
      <th mat-header-cell *matHeaderCellDef>Fee</th>
      <td mat-cell *matCellDef="let c" class="num">{{ c.fee | number }}</td>
    </ng-container>
    <ng-container matColumnDef="requested">
      <th mat-header-cell *matHeaderCellDef>Requested</th>
      <td mat-cell *matCellDef="let c" class="num strong">{{ c.requested_amount | number }}</td>
    </ng-container>
    <ng-container matColumnDef="now">
      <th mat-header-cell *matHeaderCellDef>Now</th>
      <td mat-cell *matCellDef="let c" class="num" [class.drift]="c.payable_amount !== c.requested_amount">{{ c.payable_amount | number }}</td>
    </ng-container>
    <ng-container matColumnDef="payment">
      <th mat-header-cell *matHeaderCellDef>Payment</th>
      <td mat-cell *matCellDef="let c"><span class="chip" [ngClass]="statusClass(c.payment_status)">{{ paymentLabel(c.payment_status) }}</span></td>
    </ng-container>
    <tr mat-header-row *matHeaderRowDef="contractColumns"></tr>
    <tr mat-row *matRowDef="let row; columns: contractColumns;"></tr>
  </table>
</div>
}
}
